import { SearchModes } from '@/domainLogic/index.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { css } from 'styled-components';
import {
  LISTVIEW_CONTAINER_WIDTH,
  LISTVIEW_CONTAINER_WIDTH_FROM_1024PX,
  MAP_LIST_EVOLUTION,
  MAP_LIST_WIDTH,
  MAP_LIST_WIDTH_LARGE,
} from '@/app/components/search/styles/constants.ts';
import { LAYOUT_TYPE } from '@/app/components/search/components/LayoutManager/utils.ts';
import { areTaxesIncludedInPrice } from '@/app/common/config/configuration/configurationUtilsClient.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { Localization } from '../../../common/Localization';
import { RouteType } from '../../../Curiosity';
import type { PagerProps } from '../../Pager/PagerProps';
import getSearchParams from '../../../common/getSearchParams';
import { createOffsetFromPage, createPageFromOffset, ITEMS_PER_PAGE } from '../../../utils/pagerUtils';
import type { DealCard } from '../../../utils/buildDealCards/dealCardTypes';

type GetListContainerProps = {
  dealCards: DealCard[];
  dealsCount: number;
  searchMode: SearchModes;
  isDesktop: boolean;
  isTablet: boolean;
  localization: Localization;
  offset: number;
  isLoading: boolean;
  handlePagination: (newSearchParams: SearchParams) => void;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  t: TranslationFunction;
};

export const getListContainerProps = ({
  dealCards,
  dealsCount,
  searchMode,
  isDesktop,
  offset,
  isLoading,
  localization,
  handlePagination,
  setOffset,
  t,
}: GetListContainerProps) => {
  const hasNoResults = dealCards?.length === 0;
  const isEndOfResults = dealCards?.length >= dealsCount;
  const showBottomElements = !isDesktop ? isEndOfResults : true;
  const { country, configValuesApp } = localization;

  const taxesToolTipForHO = areTaxesIncludedInPrice({ searchMode, country, configValuesApp })
    ? t('vacation.oss.components.searchresults.incl_at_hotel.footer')
    : `* ${t('vacation.oss.components.searchresults.due_at_hotel.footer')}`;
  const resultsHaveTsh = dealCards && dealCards?.filter((dealCard) => dealCard?.product?.rate?.flags?.tsh).length > 0;
  const resultsHaveMyprice =
    dealCards && dealCards?.filter((dealCard) => dealCard?.product?.rate?.flags?.myPrice).length > 0;
  const firstDisclaimerText =
    searchMode === SearchModes.DP
      ? `* ${t('vacation.oss.components.searchresults.due_at_hotel.footer')}`
      : searchMode === SearchModes.TH
        ? `* ${t('vacation.oss.components.searchresults.due_at_hotel.footer')}`
        : taxesToolTipForHO;
  const hideSecondDisclaimer = hasNoResults || !showBottomElements;
  const secondDisclaimerText =
    searchMode === SearchModes.DP
      ? t('vacation.oss.components.searchresults.price_last_update.footer_dp')
      : searchMode === SearchModes.TH
        ? t('vacation.oss.components.searchresults.price_last_update.footer_th')
        : t('vacation.oss.components.searchresults.price_last_update.footer_ho');
  const hideTshDisclaimer = hasNoResults || !resultsHaveTsh;
  const hideMypriceDisclaimer = hasNoResults || !resultsHaveMyprice;
  const listContainerClassName = 'list-container';

  const pagerProps: PagerProps = {
    total: dealsCount,
    itemPerPage: ITEMS_PER_PAGE,
    currentPage: createPageFromOffset(offset),
    routeType: RouteType.OSMAP,
    hide: !isDesktop && (hasNoResults || isEndOfResults),
    isLoading,
    callback: (page: number) => {
      if (page !== createPageFromOffset(offset)) {
        const pagerOffset = createOffsetFromPage(page);
        setOffset(pagerOffset);
        const searchObject = getSearchParams();
        const newSearchParams = { ...searchObject, [URLParameters.PAGE]: `${page}` };
        handlePagination(newSearchParams);
      }
    },
  };

  return {
    hasNoResults,
    isEndOfResults,
    showBottomElements,
    hideFirstDisclaimer: true, // this information is now displayed inside the tooltip
    firstDisclaimerText,
    hideSecondDisclaimer,
    secondDisclaimerText,
    hideTshDisclaimer,
    hideMypriceDisclaimer,
    listContainerClassName,
    pagerProps,
  };
};

export const hasDealsWithVoucherExtra = (productsData: ProductsData) => !!productsData?.flags?.voucherExtra;

export const getListWidth = (listView?: boolean | null, evolutionResultsOnlyEnabled?: boolean) => {
  if (listView) {
    return css`
      @media (min-width: 1024px) {
        width: ${LISTVIEW_CONTAINER_WIDTH_FROM_1024PX};
      }

      @media (min-width: 1280px) {
        width: ${LISTVIEW_CONTAINER_WIDTH};
      }
    `;
  }

  if (evolutionResultsOnlyEnabled) {
    return css`
      @media (min-width: 1024px) {
        width: ${MAP_LIST_EVOLUTION};
      }
    `;
  }

  return css`
    @media (min-width: 1024px) {
      width: ${MAP_LIST_WIDTH};
    }
    @media (min-width: 1280px) {
      width: ${MAP_LIST_WIDTH_LARGE};
    }
  `;
};

export const getListWidthFromType = (layoutType: LAYOUT_TYPE) => {
  if (layoutType === LAYOUT_TYPE.DESKTOP_LIST) {
    return css`
      @media (min-width: 1024px) {
        width: ${LISTVIEW_CONTAINER_WIDTH_FROM_1024PX};
      }

      @media (min-width: 1280px) {
        width: ${LISTVIEW_CONTAINER_WIDTH};
      }
    `;
  }

  if (layoutType === LAYOUT_TYPE.DESKTOP_MAP) {
    return css`
      @media (min-width: 1024px) {
        width: ${MAP_LIST_WIDTH};
      }
      @media (min-width: 1280px) {
        width: ${MAP_LIST_WIDTH_LARGE};
      }
    `;
  }

  return css``;
};
